import DISPLAY_CURRENCIES from "./displayCurrencies.json";

/**
 * THE EARNING DEMOS' WORDS AND NUMBERS, IN ONE MODULE (kickoff refinement C).
 *
 * Both hosts, the Kickoff rules scene and the ChallengePreview earning beats,
 * render the same three demos with the same approved words. Each host still
 * decides which beats a learner is eligible for; nothing here reads a store.
 *
 * THE APPROVED COPY IS AMIR'S, exact strings (plan, "Approved copy"). They are
 * pinned by `approvedCopy.pin.test.js` against literals typed in that test, so
 * editing a constant here without editing the ruled list fails the build.
 */
export const APPROVED_EARNING_COPY = Object.freeze({
  xp: "earn xp. lift your squad.",
  cup: "most squad xp wins the cup.",
  vault: "the vault is real money.",
  vaultSupport: "your progress helps grow the prize.",
  solo: "your work earns xp.",
});

/** The visible label every demo carries (ADR-012: illustrative values say so). */
export const EXAMPLE_CHIP = "example";

/** D2: the XP example, `+20 xp`, 120 to 140. */
export const EXAMPLE_XP = Object.freeze({ from: 120, to: 140, gain: 20 });

/**
 * D2: the Vault example, in MAJOR units of the cohort's currency (120 to 360),
 * never minor units, formatted with no fraction digits so JPY and KWD both
 * read `120`.
 */
export const EXAMPLE_VAULT = Object.freeze({ from: 120, to: 360 });

/**
 * CURRENCY VALIDATION, DEFENCE IN DEPTH (plan, Currency, r3).
 *
 * The backend (BE0) is authoritative: it serves `null` for anything outside its
 * allowlist. This re-checks against a byte-identical copy of that list
 * (`displayCurrencies.json`, sha256 pinned by `displayCurrencies.pin.test.js`).
 * `Intl.NumberFormat` acceptance is NOT validation (it formats `XXX 120` and
 * `ZZZ 120`), and `Intl.supportedValuesOf` is not consulted at all.
 */
const ALLOWED = new Set(DISPLAY_CURRENCIES);

export const isDisplayCurrency = (unit) =>
  typeof unit === "string" && ALLOWED.has(unit);

const format = (major, unit, currencyDisplay) => {
  if (!isDisplayCurrency(unit)) return null;
  try {
    const text = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: unit,
      currencyDisplay,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(major);
    return typeof text === "string" && text.length > 0 ? text : null;
  } catch (_err) {
    // A missing or broken Intl gives the numberless fill, never a guess.
    return null;
  }
};

/**
 * The example amount as painted: `$120` for USD, `CAD 120` for any other
 * listed code, or `null` for an unknown, absent or unlisted unit (the caller
 * then draws the numberless fill).
 */
export const exampleMoney = (major, unit) =>
  format(major, unit, unit === "USD" ? "narrowSymbol" : "code");

/** The same amount for assistive technology: "120 US dollars". */
export const exampleMoneySpoken = (major, unit) => format(major, unit, "name");

/**
 * THE ONE ACCESSIBLE SENTENCE PER DEMO. It says "example" and, when known, the
 * spoken amount. It never repeats the headline, which the host renders once.
 */
export const earningSentence = (beatId, { unit = null, solo = false } = {}) => {
  if (beatId === "xp") {
    return solo
      ? `example: plus ${EXAMPLE_XP.gain} xp takes your total from ${EXAMPLE_XP.from} to ${EXAMPLE_XP.to}.`
      : `example: plus ${EXAMPLE_XP.gain} xp takes your total from ${EXAMPLE_XP.from} to ${EXAMPLE_XP.to}, and your squad's bar rises.`;
  }
  if (beatId === "cup") {
    return "example: three squads, and your squad moves into first place.";
  }
  if (beatId === "vault") {
    const from = exampleMoneySpoken(EXAMPLE_VAULT.from, unit);
    const to = exampleMoneySpoken(EXAMPLE_VAULT.to, unit);
    return from && to
      ? `example: the vault grows from ${from} to ${to}.`
      : "example: the vault fills up.";
  }
  return "";
};

/**
 * THE KICKOFF'S BEATS (plan M4: the host owns eligibility).
 *
 *   competition off  [xp] with the solo headline
 *   competition on   [xp, cup]
 *   plus a prize     [xp, cup, vault]
 *
 * The Preview does NOT use this; it keeps its own independent flags.
 */
export const kickoffBeats = ({ showCompetition, showVault } = {}) => {
  if (showCompetition !== true) {
    return [{ id: "xp", headline: APPROVED_EARNING_COPY.solo, solo: true }];
  }
  const beats = [
    { id: "xp", headline: APPROVED_EARNING_COPY.xp, solo: false },
    { id: "cup", headline: APPROVED_EARNING_COPY.cup, solo: false },
  ];
  if (showVault === true) {
    beats.push({
      id: "vault",
      headline: APPROVED_EARNING_COPY.vault,
      support: APPROVED_EARNING_COPY.vaultSupport,
      solo: false,
    });
  }
  return beats;
};
